import React from 'react';
import { Search, X } from 'lucide-react';
import { PortalPopover } from '../ui/PortalPopover';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { FilterSearchBarProps } from './filter/types';
import { useFilterSearchState } from './filter/useFilterSearchState';
import { FilterSearchBadges } from './filter/FilterSearchBadges';
import { FilterSearchColumnMenu } from './filter/FilterSearchColumnMenu';
import { FilterSearchOperatorMenu } from './filter/FilterSearchOperatorMenu';
import { FilterSearchValueMenu } from './filter/FilterSearchValueMenu';

export type { FilterSearchBarProps } from './filter/types';

export function FilterSearchBar<T extends Record<string, any> = any>({
  columns,
  filterRules,
  onFilterRulesChange,
  searchValue,
  onSearchChange,
  placeholder = 'Search or filter by column... (/)',
  className = '',
  inputRef: externalInputRef,
}: FilterSearchBarProps<T>) {
  const {
    isFocused,
    setIsFocused,
    dropdownOpen,
    setDropdownOpen,
    inputValue,
    setInputValue,
    stage,
    setStage,
    pendingColumn,
    pendingOperator,
    highlightedIndex,
    setHighlightedIndex,
    containerRef,
    localInputRef,
    setInputRef,
    matchingColumns,
    matchingBooleanOptions,
    matchingEnumOptions,
    matchingOperators,
    matchingOptions,
    pendingOptions,
    handleSelectColumn,
    handleCommitBoolean,
    handleCommitEnum,
    handleSelectOperator,
    handleCommitValue,
    handleCommitSearchText,
    resetPendingState,
    handleRemoveFilterRule,
    handleRemoveSearchText,
    handleKeyDown,
  } = useFilterSearchState({
    columns,
    filterRules,
    onFilterRulesChange,
    searchValue,
    onSearchChange,
    externalInputRef,
  });

  return (
    <div
      ref={containerRef}
      onClick={() => localInputRef.current?.focus()}
      className={`relative flex items-center flex-wrap gap-1.5 px-2.5 py-1 min-h-[30px] rounded border bg-cf-bg transition-colors cursor-text ${
        isFocused
          ? 'border-cf-orange ring-1 ring-cf-orange/30'
          : 'border-cf-border hover:border-cf-textMuted'
      } ${className}`}
    >
      {/* Search Lens Icon */}
      <Search size={13} className="text-cf-textMuted shrink-0 pointer-events-none" />

      {/* Committed Filter Badges, Freeform Search Badge & Pending Pill */}
      <FilterSearchBadges
        columns={columns}
        filterRules={filterRules}
        searchValue={searchValue}
        pendingColumn={pendingColumn}
        pendingOperator={pendingOperator}
        onRemoveFilterRule={handleRemoveFilterRule}
        onRemoveSearchText={handleRemoveSearchText}
        onResetPendingState={resetPendingState}
        onSelectSearchText={(val) => {
          setInputValue(val);
          onSearchChange('');
          localInputRef.current?.focus();
        }}
      />

      {/* Main Text Input */}
      <div className="flex-1 min-w-[90px]">
        <Input
          ref={setInputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setDropdownOpen(true);
          }}
          onFocus={() => {
            setIsFocused(true);
            setDropdownOpen(true);
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          onKeyDown={handleKeyDown}
          placeholder={
            pendingColumn?.dataType === 'date' && stage === 'value'
              ? 'YYYY-MM-DD (e.g. 2026-09-10) or choose below...'
              : filterRules.length > 0 || searchValue || pendingColumn
              ? ''
              : placeholder
          }
          className="h-6 !bg-transparent bg-transparent !border-none !outline-none text-xs text-cf-textPrimary placeholder:text-cf-textMuted font-sans !p-0 !ring-0"
          style={{ backgroundColor: 'transparent' }}
        />
      </div>

      {/* Clear All Button */}
      {(filterRules.length > 0 || searchValue || inputValue) && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={(e) => {
            e.stopPropagation();
            onFilterRulesChange([]);
            onSearchChange('');
            resetPendingState();
          }}
          className="text-cf-textMuted hover:text-cf-textPrimary shrink-0"
          title="Clear search and all filters"
        >
          <X size={12} />
        </Button>
      )}

      {/* Suggestion Dropdown via PortalPopover */}
      <PortalPopover
        isOpen={dropdownOpen}
        onClose={() => setDropdownOpen(false)}
        triggerRef={containerRef}
        align="left"
        className="w-[300px] sm:w-[340px] bg-cf-surface border border-cf-border rounded-lg shadow-2xl p-1.5 font-sans text-xs select-none"
      >
        {stage === 'column' && (
          <FilterSearchColumnMenu
            matchingColumns={matchingColumns}
            inputValue={inputValue}
            highlightedIndex={highlightedIndex}
            onSelectColumn={handleSelectColumn}
            onCommitSearchText={handleCommitSearchText}
            onHoverIndex={setHighlightedIndex}
          />
        )}

        {stage === 'operator' && pendingColumn && (
          <FilterSearchOperatorMenu
            pendingColumn={pendingColumn}
            matchingOperators={matchingOperators}
            highlightedIndex={highlightedIndex}
            onSelectOperator={handleSelectOperator}
            onBackToColumnStage={() => setStage('column')}
            onHoverIndex={setHighlightedIndex}
          />
        )}

        {(stage === 'boolean' || stage === 'enum' || stage === 'value') && pendingColumn && (
          <FilterSearchValueMenu
            stage={stage}
            pendingColumn={pendingColumn}
            pendingOperator={pendingOperator}
            inputValue={inputValue}
            highlightedIndex={highlightedIndex}
            matchingBooleanOptions={matchingBooleanOptions}
            matchingEnumOptions={matchingEnumOptions}
            pendingOptions={pendingOptions}
            matchingOptions={matchingOptions}
            onCommitBoolean={handleCommitBoolean}
            onCommitEnum={handleCommitEnum}
            onCommitValue={handleCommitValue}
            onBackToColumnStage={() => setStage('column')}
            onBackToOperatorStage={() => setStage('operator')}
            onHoverIndex={setHighlightedIndex}
          />
        )}
      </PortalPopover>
    </div>
  );
}
